import React, { useState, useEffect, useCallback } from "react"
import { Col, Row, Nav, NavItem, NavLink, TabContent, TabPane, Spinner, UncontrolledAlert } from "reactstrap"
import { AvForm, AvField } from "availity-reactstrap-validation"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const SmsSettings = () => {
  const { canEdit, MODULE } = usePermissions()
  const MID = MODULE.SMS_SETTINGS

  const [activeTab, setActiveTab] = useState("1")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [settings, setSettings] = useState(null)
  const [templates, setTemplates] = useState([])
  const [smsProviders, setSmsProviders] = useState({})
  const [waProviders, setWaProviders] = useState({})
  const [previewData, setPreviewData] = useState(null)
  const [msg, setMsg] = useState(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [settingsRes, templatesRes, smsP, waP] = await Promise.all([
        api.get("sms-settings"),
        api.get("sms-templates"),
        api.get("sms-settings/providers"),
        api.get("sms-settings/wa-providers"),
      ])
      setSettings(settingsRes.data?.data || null)
      setTemplates(Array.isArray(templatesRes.data?.data) ? templatesRes.data.data : [])
      setSmsProviders(smsP.data?.data || {})
      setWaProviders(waP.data?.data || {})
    } catch (e) { console.error(e) }
    setLoading(false)
  }, [])

  useEffect(() => { loadData() }, [loadData])

  // ── Save Settings ──
  const handleSaveSettings = async (e, values) => {
    setSaving(true); setMsg(null)
    try {
      const resp = await api.post("sms-settings", values)
      if (resp.data.success) { setMsg({ type: "success", text: "Settings saved!" }); loadData() }
      else setMsg({ type: "danger", text: resp.data.error || "Failed to save" })
    } catch (err) { setMsg({ type: "danger", text: err.message }) }
    setSaving(false)
  }

  // ── Test SMS ──
  const handleTestSms = async () => {
    const mobile = document.getElementById("testMobile")?.value
    if (!mobile) { setTestResult({ type: "danger", text: "Enter mobile number" }); return }
    setTesting(true); setTestResult(null)
    try {
      const resp = await api.post("sms-settings/test-sms", { mobile })
      setTestResult({ type: resp.data.success ? "success" : "danger", text: resp.data.success ? "Test SMS sent!" : (resp.data.data?.error || "Failed") })
    } catch (err) { setTestResult({ type: "danger", text: err.message }) }
    setTesting(false)
  }

  // ── Test WhatsApp ──
  const handleTestWa = async () => {
    const mobile = document.getElementById("testMobileWa")?.value
    if (!mobile) { setTestResult({ type: "danger", text: "Enter mobile number" }); return }
    setTesting(true); setTestResult(null)
    try {
      const resp = await api.post("sms-settings/test-whatsapp", { mobile })
      setTestResult({ type: resp.data.success ? "success" : "danger", text: resp.data.success ? "Test WhatsApp sent!" : (resp.data.data?.error || "Failed") })
    } catch (err) { setTestResult({ type: "danger", text: err.message }) }
    setTesting(false)
  }

  // ── Save Template ──
  const handleSaveTemplate = async (tmpl) => {
    try {
      await api.post("sms-templates", tmpl)
      setMsg({ type: "success", text: "Template saved!" })
      loadData()
    } catch (err) { setMsg({ type: "danger", text: err.message }) }
  }

  // ── Preview Template ──
  const handlePreview = async (body, type) => {
    try {
      const resp = await api.post("sms-templates/preview", { template_body: body, template_type: type })
      setPreviewData(resp.data?.data || null)
    } catch (e) { console.error(e) }
  }

  const templateLabels = {
    purchase_receipt: { label: "Purchase Receipt", icon: "fa-receipt", color: "#6366f1", desc: "Auto-sent after each VLCC milk purchase" },
    daily_summary: { label: "Daily Summary", icon: "fa-chart-bar", color: "#0ea5e9", desc: "End-of-session collection summary" },
    bill_payment: { label: "Bill Payment", icon: "fa-money-bill-wave", color: "#22c55e", desc: "Monthly bill payment notification" },
    vendor_bill: { label: "Vendor Bill", icon: "fa-store", color: "#f59e0b", desc: "Vendor bill payment notification" },
    loan_deduction: { label: "Loan Deduction", icon: "fa-hand-holding-usd", color: "#ef4444", desc: "Loan EMI deduction alert" },
    bonus_credit: { label: "Bonus Credit", icon: "fa-gift", color: "#10b981", desc: "Bonus/incentive credit notification" },
    recovery_notice: { label: "Recovery Notice", icon: "fa-exclamation-triangle", color: "#f97316", desc: "Recovery deduction notice" },
    custom: { label: "Custom Message", icon: "fa-comment-dots", color: "#8b5cf6", desc: "Manual custom SMS" },
  }

  if (loading) return <div style={{textAlign:"center",padding:"60px"}}><Spinner color="primary" /><p style={{marginTop:"12px",color:"#6c757d"}}>Loading SMS Settings...</p></div>

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-sms" style={{color:"#6366f1",marginRight:"8px"}}></i>SMS & WhatsApp Settings</h4></Col>
        </Row>
      </div>
      <div className="dashContent" style={{padding:"0"}}>
        {msg && <UncontrolledAlert color={msg.type} style={{margin:"16px 16px 0"}}><strong>{msg.text}</strong></UncontrolledAlert>}

        {/* Tabs */}
        <Nav tabs style={{padding:"0 16px",borderBottom:"2px solid #e5e7eb"}}>
          {[{id:"1",icon:"fa-cog",label:"Provider Setup"},{id:"2",icon:"fa-file-alt",label:"Templates"},{id:"3",icon:"fa-toggle-on",label:"Automation"}].map(t=>(
            <NavItem key={t.id}>
              <NavLink className={activeTab===t.id?"active":""} onClick={()=>setActiveTab(t.id)} style={{cursor:"pointer",fontWeight:activeTab===t.id?"700":"400",color:activeTab===t.id?"#6366f1":"#6c757d",borderBottom:activeTab===t.id?"3px solid #6366f1":"none",background:"transparent",padding:"12px 20px"}}>
                <i className={`fas ${t.icon}`} style={{marginRight:"6px"}}></i>{t.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <TabContent activeTab={activeTab} style={{padding:"20px"}}>
          {/* ══════ TAB 1: Provider Setup ══════ */}
          <TabPane tabId="1">
            <AvForm onValidSubmit={handleSaveSettings}>
              {/* Channel Mode */}
              <div style={{background:"#f8f9fa",borderRadius:"12px",padding:"20px",marginBottom:"20px",border:"1px solid #e5e7eb"}}>
                <h6 style={{fontWeight:"700",marginBottom:"16px"}}><i className="fas fa-broadcast-tower" style={{color:"#6366f1",marginRight:"8px"}}></i>Channel Mode</h6>
                <Row>
                  <Col md={6}>
                    <AvField type="select" name="channel_mode" label="Messaging Channel" value={settings?.channel_mode||"sms"}>
                      <option value="sms">SMS Only</option>
                      <option value="whatsapp">WhatsApp Only</option>
                      <option value="whatsapp_sms">WhatsApp + SMS Fallback (Recommended)</option>
                    </AvField>
                  </Col>
                  <Col md={6}>
                    <AvField type="select" name="language" label="Language" value={settings?.language||"en"}>
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                      <option value="te">Telugu</option>
                      <option value="kn">Kannada</option>
                    </AvField>
                  </Col>
                </Row>
              </div>

              {/* SMS Provider */}
              <div style={{background:"#f8f9fa",borderRadius:"12px",padding:"20px",marginBottom:"20px",border:"1px solid #e5e7eb"}}>
                <h6 style={{fontWeight:"700",marginBottom:"16px"}}><i className="fas fa-sms" style={{color:"#22c55e",marginRight:"8px"}}></i>SMS Provider</h6>
                <Row>
                  <Col md={4}>
                    <AvField type="select" name="sms_provider" label="Select Provider" value={settings?.sms_provider||""}>
                      <option value="">-- Select --</option>
                      {Object.entries(smsProviders).map(([k,v])=><option key={k} value={k}>{v.name} ({v.pricing})</option>)}
                    </AvField>
                  </Col>
                  <Col md={4}>
                    <AvField name="sms_api_key" label="API Key / Auth Token" type="password" value="" placeholder={settings?.sms_api_key_set ? "••••••••(set)" : "Enter API key"} />
                  </Col>
                  <Col md={4}>
                    <AvField name="sms_api_secret" label="API Secret (if needed)" type="password" value="" placeholder={settings?.sms_api_secret_set ? "••••••••(set)" : "Optional"} />
                  </Col>
                  <Col md={3}>
                    <AvField name="sms_sender_id" label="Sender ID (6 chars)" value={settings?.sms_sender_id||""} maxLength="6" placeholder="DAIRYM" />
                  </Col>
                  <Col md={3}>
                    <AvField name="sms_entity_id" label="DLT Entity ID" value={settings?.sms_entity_id||""} placeholder="TRAI Entity ID" />
                  </Col>
                  <Col md={3}>
                    <AvField type="select" name="sms_route" label="Route" value={settings?.sms_route||"4"}>
                      <option value="4">Transactional (Route 4)</option>
                      <option value="1">Promotional (Route 1)</option>
                    </AvField>
                  </Col>
                  <Col md={3}>
                    <label>&nbsp;</label>
                    <div>
                      <input type="text" id="testMobile" placeholder="Test mobile" className="form-control form-control-sm" style={{display:"inline-block",width:"55%",marginRight:"4px"}}/>
                      <button type="button" className="btn btn-sm btn-outline-success" onClick={handleTestSms} disabled={testing}>{testing?<Spinner size="sm"/>:"Test SMS"}</button>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* WhatsApp Provider */}
              <div style={{background:"#f8f9fa",borderRadius:"12px",padding:"20px",marginBottom:"20px",border:"1px solid #e5e7eb"}}>
                <h6 style={{fontWeight:"700",marginBottom:"16px"}}><i className="fab fa-whatsapp" style={{color:"#25D366",marginRight:"8px"}}></i>WhatsApp Provider</h6>
                <Row>
                  <Col md={4}>
                    <AvField type="select" name="wa_provider" label="Select Provider" value={settings?.wa_provider||""}>
                      <option value="">-- Select --</option>
                      {Object.entries(waProviders).map(([k,v])=><option key={k} value={k}>{v.name} ({v.pricing})</option>)}
                    </AvField>
                  </Col>
                  <Col md={4}>
                    <AvField name="wa_api_key" label="API Key / Access Token" type="password" value="" placeholder={settings?.wa_api_key_set ? "••••••••(set)" : "Enter API key"} />
                  </Col>
                  <Col md={4}>
                    <AvField name="wa_api_secret" label="API Secret (if needed)" type="password" value="" placeholder={settings?.wa_api_secret_set ? "••••••••(set)" : "Optional"} />
                  </Col>
                  <Col md={4}>
                    <AvField name="wa_phone_number_id" label="Phone Number ID" value={settings?.wa_phone_number_id||""} placeholder="From provider dashboard" />
                  </Col>
                  <Col md={4}>
                    <AvField name="wa_business_account_id" label="Business Account ID" value={settings?.wa_business_account_id||""} placeholder="Optional" />
                  </Col>
                  <Col md={4}>
                    <label>&nbsp;</label>
                    <div>
                      <input type="text" id="testMobileWa" placeholder="Test mobile" className="form-control form-control-sm" style={{display:"inline-block",width:"55%",marginRight:"4px"}}/>
                      <button type="button" className="btn btn-sm btn-outline-success" onClick={handleTestWa} disabled={testing}>{testing?<Spinner size="sm"/>:<><i className="fab fa-whatsapp"></i> Test</>}</button>
                    </div>
                  </Col>
                </Row>
              </div>

              {testResult && <UncontrolledAlert color={testResult.type}>{testResult.text}</UncontrolledAlert>}

              {/* Active Toggle + Save */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 0",borderTop:"1px solid #e5e7eb"}}>
                <label style={{display:"flex",alignItems:"center",gap:"10px",cursor:"pointer",fontSize:"15px"}}>
                  <input type="checkbox" name="is_active" defaultChecked={settings?.is_active} style={{width:"20px",height:"20px",accentColor:"#22c55e"}} />
                  <span style={{fontWeight:"600"}}>Enable SMS/WhatsApp Messaging</span>
                </label>
                {canEdit(MID) && <button type="submit" className="btn btn-primary" disabled={saving}>{saving?<><Spinner size="sm"/> Saving...</>:<><i className="fas fa-save"></i> Save Settings</>}</button>}
              </div>
            </AvForm>
          </TabPane>

          {/* ══════ TAB 2: Templates ══════ */}
          <TabPane tabId="2">
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(360px,1fr))",gap:"16px"}}>
              {templates.map((tmpl,i) => {
                const meta = templateLabels[tmpl.template_type] || {label:tmpl.template_type,icon:"fa-comment",color:"#6c757d",desc:""}
                return (
                  <div key={i} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:"12px",overflow:"hidden",boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}>
                    <div style={{background:meta.color,color:"#fff",padding:"12px 16px",display:"flex",alignItems:"center",gap:"10px"}}>
                      <i className={`fas ${meta.icon}`} style={{fontSize:"18px"}}></i>
                      <div>
                        <div style={{fontWeight:"700",fontSize:"14px"}}>{meta.label}</div>
                        <div style={{fontSize:"11px",opacity:0.85}}>{meta.desc}</div>
                      </div>
                    </div>
                    <div style={{padding:"16px"}}>
                      <textarea value={tmpl.template_body||""} onChange={e=>{const u=[...templates];u[i]={...u[i],template_body:e.target.value};setTemplates(u)}} rows={5} className="form-control" style={{fontFamily:"monospace",fontSize:"12px",background:"#f8f9fa",marginBottom:"8px"}}/>
                      <Row>
                        <Col md={6}><input type="text" className="form-control form-control-sm" placeholder="DLT Template ID" value={tmpl.dlt_template_id||""} onChange={e=>{const u=[...templates];u[i]={...u[i],dlt_template_id:e.target.value};setTemplates(u)}}/></Col>
                        <Col md={6} style={{textAlign:"right"}}>
                          <button className="btn btn-sm btn-outline-primary" style={{marginRight:"4px"}} onClick={()=>handlePreview(tmpl.template_body,tmpl.template_type)}><i className="fas fa-eye"></i> Preview</button>
                          <button className="btn btn-sm btn-primary" onClick={()=>handleSaveTemplate(tmpl)}><i className="fas fa-save"></i> Save</button>
                        </Col>
                      </Row>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Preview Panel */}
            {previewData && (
              <div style={{marginTop:"20px",background:"#1e293b",color:"#e2e8f0",borderRadius:"12px",padding:"20px"}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:"12px"}}>
                  <h6 style={{color:"#94a3b8",margin:0}}>📱 Preview</h6>
                  <span style={{color:"#94a3b8",fontSize:"12px"}}>{previewData.characters} chars • {previewData.sms_parts} SMS part{previewData.sms_parts>1?"s":""}</span>
                </div>
                <pre style={{color:"#f1f5f9",margin:0,whiteSpace:"pre-wrap",fontFamily:"monospace",fontSize:"13px",lineHeight:"1.6"}}>{previewData.preview}</pre>
              </div>
            )}
          </TabPane>

          {/* ══════ TAB 3: Automation ══════ */}
          <TabPane tabId="3">
            <AvForm onValidSubmit={handleSaveSettings}>
              <div style={{background:"#f8f9fa",borderRadius:"12px",padding:"24px",border:"1px solid #e5e7eb"}}>
                <h6 style={{fontWeight:"700",marginBottom:"20px"}}><i className="fas fa-robot" style={{color:"#6366f1",marginRight:"8px"}}></i>Auto-Trigger Settings</h6>
                <p style={{color:"#6c757d",fontSize:"13px",marginBottom:"20px"}}>Control which events automatically send SMS/WhatsApp messages</p>

                {[
                  {name:"auto_purchase_msg",label:"Purchase Receipt SMS",desc:"Auto-send to producer after every VLCC milk purchase entry",icon:"fa-receipt",color:"#6366f1",level:"VLCC (Daily)"},
                  {name:"auto_summary_msg",label:"Daily Summary SMS",desc:"Send collection summary at end of each session",icon:"fa-chart-bar",color:"#0ea5e9",level:"VLCC (Daily)"},
                  {name:"payment_msg",label:"Bill Payment SMS",desc:"Send payment notification when bills are processed",icon:"fa-money-bill-wave",color:"#22c55e",level:"Company (Monthly)"},
                  {name:"loan_deduction_msg",label:"Loan Deduction Alert",desc:"Alert producer when loan EMI is deducted",icon:"fa-hand-holding-usd",color:"#ef4444",level:"Company"},
                  {name:"recovery_msg",label:"Recovery Notice",desc:"Notify when recovery amount is deducted",icon:"fa-exclamation-triangle",color:"#f97316",level:"Company"},
                  {name:"bonus_msg",label:"Bonus Credit Alert",desc:"Alert when bonus/incentive is credited",icon:"fa-gift",color:"#10b981",level:"Company"},
                ].map(item => (
                  <label key={item.name} style={{display:"flex",alignItems:"center",gap:"16px",padding:"16px",marginBottom:"8px",background:"#fff",borderRadius:"10px",border:"1px solid #e5e7eb",cursor:"pointer",transition:"all 0.2s"}}>
                    <input type="checkbox" name={item.name} defaultChecked={settings?.[item.name]!==false} style={{width:"22px",height:"22px",accentColor:item.color,flexShrink:0}} />
                    <i className={`fas ${item.icon}`} style={{fontSize:"20px",color:item.color,width:"24px",textAlign:"center"}}></i>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:"600",fontSize:"14px"}}>{item.label}</div>
                      <div style={{color:"#6c757d",fontSize:"12px"}}>{item.desc}</div>
                    </div>
                    <span className="badge" style={{background:item.level.includes("VLCC")?"#dbeafe":"#dcfce7",color:item.level.includes("VLCC")?"#1e40af":"#166534",fontSize:"11px",padding:"4px 10px",borderRadius:"20px"}}>{item.level}</span>
                  </label>
                ))}
              </div>

              {canEdit(MID) && <div style={{textAlign:"right",marginTop:"16px"}}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?"Saving...":"Save Automation Settings"}</button>
              </div>}
            </AvForm>
          </TabPane>
        </TabContent>
      </div>
    </div>
  )
}

export default SmsSettings
